import React, { useState } from 'react';
import { XIcon, CalendarIcon, ClockIcon, UserIcon, MailIcon, PhoneIcon, HomeIcon, CheckCircleIcon } from 'lucide-react';
interface ServiceBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  serviceType: 'photography' | 'valuation';
  packageName: string;
  price: number;
}
const ServiceBookingModal: React.FC<ServiceBookingModalProps> = ({
  isOpen,
  onClose,
  serviceType,
  packageName,
  price
}) => {
  const [step, setStep] = useState<'form' | 'success'>('form');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyAddress: '',
    propertyType: 'house',
    preferredDate: '',
    preferredTime: '',
    additionalNotes: ''
  });
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Booking submitted:', {
      serviceType,
      packageName,
      price,
      ...formData
    });
    setStep('success');
  };
  const handleClose = () => {
    setStep('form');
    setFormData({
      name: '',
      email: '',
      phone: '',
      propertyAddress: '',
      propertyType: 'house',
      preferredDate: '',
      preferredTime: '',
      additionalNotes: ''
    });
    onClose();
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-293">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0" data-id="element-294">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-75" onClick={handleClose} data-id="element-295" />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full" data-id="element-296">
          {step === 'form' ? <>
              {/* Header */}
              <div className="bg-gradient-to-r from-primary to-[rgb(var(--color-primary-dark))] px-6 py-6" data-id="element-297">
                <div className="flex items-center justify-between" data-id="element-298">
                  <div data-id="element-299">
                    <h3 className="text-2xl font-bold text-white" data-id="element-300">
                      {serviceType === 'photography' ? 'Reservar Fotografia' : 'Solicitar Avaliação'}
                    </h3>
                    <p className="text-white/90 mt-1" data-id="element-301">
                      {packageName} - ${price}
                    </p>
                  </div>
                  <button onClick={handleClose} className="text-white/80 hover:text-white transition-colors" data-id="element-302">
                    <XIcon className="h-6 w-6" data-id="element-303" />
                  </button>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="px-6 py-6" data-id="element-304">
                <div className="space-y-5" data-id="element-305">
                  {/* Personal Information */}
                  <div data-id="element-306">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-307">
                      Informações Pessoais
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-308">
                      <div data-id="element-309">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-310">
                          Nome Completo *
                        </label>
                        <div className="relative" data-id="element-311">
                          <UserIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" data-id="element-312" />
                          <input type="text" name="name" value={formData.name} onChange={handleChange} required className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" placeholder="Seu nome" data-id="element-313" />
                        </div>
                      </div>
                      <div data-id="element-314">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-315">
                          Email *
                        </label>
                        <div className="relative" data-id="element-316">
                          <MailIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" data-id="element-317" />
                          <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" placeholder="seu@email.com" data-id="element-318" />
                        </div>
                      </div>
                      <div data-id="element-319">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-320">
                          Telefone *
                        </label>
                        <div className="relative" data-id="element-321">
                          <PhoneIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" data-id="element-322" />
                          <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" placeholder="(00) 00000-0000" data-id="element-323" />
                        </div>
                      </div>
                      <div data-id="element-324">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-325">
                          Tipo de Imóvel *
                        </label>
                        <select name="propertyType" value={formData.propertyType} onChange={handleChange} required className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" data-id="element-326">
                          <option value="house" data-id="element-327">Casa</option>
                          <option value="apartment" data-id="element-328">Apartamento</option>
                          <option value="land" data-id="element-329">Terreno</option>
                          <option value="commercial" data-id="element-330">Comercial</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Property Information */}
                  <div data-id="element-331">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-332">
                      Informações do Imóvel
                    </h4>
                    <div data-id="element-333">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-334">
                        Endereço do Imóvel *
                      </label>
                      <div className="relative" data-id="element-335">
                        <HomeIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" data-id="element-336" />
                        <input type="text" name="propertyAddress" value={formData.propertyAddress} onChange={handleChange} required className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" placeholder="Rua, número, bairro, cidade" data-id="element-337" />
                      </div>
                    </div>
                  </div>

                  {/* Schedule */}
                  <div data-id="element-338">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-339">
                      Agendamento Preferencial
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-340">
                      <div data-id="element-341">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-342">
                          Data Preferida *
                        </label>
                        <div className="relative" data-id="element-343">
                          <CalendarIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" data-id="element-344" />
                          <input type="date" name="preferredDate" value={formData.preferredDate} onChange={handleChange} required min={new Date().toISOString().split('T')[0]} className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" data-id="element-345" />
                        </div>
                      </div>
                      <div data-id="element-346">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-347">
                          Horário Preferido *
                        </label>
                        <div className="relative" data-id="element-348">
                          <ClockIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" data-id="element-349" />
                          <select name="preferredTime" value={formData.preferredTime} onChange={handleChange} required className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" data-id="element-350">
                            <option value="" data-id="element-351">Selecione um horário</option>
                            <option value="morning" data-id="element-352">Manhã (8h - 12h)</option>
                            <option value="afternoon" data-id="element-353">Tarde (12h - 18h)</option>
                            <option value="evening" data-id="element-354">Noite (18h - 20h)</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Additional Notes */}
                  <div data-id="element-355">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-356">
                      Observações Adicionais
                    </label>
                    <textarea name="additionalNotes" value={formData.additionalNotes} onChange={handleChange} rows={3} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" placeholder="Alguma informação adicional que devemos saber?" data-id="element-357" />
                  </div>
                </div>

                {/* Footer */}
                <div className="mt-8 flex flex-col sm:flex-row gap-3" data-id="element-358">
                  <button type="button" onClick={handleClose} className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors" data-id="element-359">
                    Cancelar
                  </button>
                  <button type="submit" className="flex-1 btn-primary" data-id="element-360">
                    Confirmar Reserva
                  </button>
                </div>
              </form>
            </> /* Success State */ : <div className="px-6 py-12 text-center" data-id="element-361">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-362">
                <CheckCircleIcon className="h-10 w-10 text-green-600" data-id="element-363" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-364">
                Reserva Confirmada!
              </h3>
              <p className="text-gray-600 mb-2" data-id="element-365">
                Sua solicitação foi recebida com sucesso.
              </p>
              <p className="text-gray-600 mb-8" data-id="element-366">
                Entraremos em contato em até 24 horas para confirmar os
                detalhes.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6" data-id="element-367">
                <p className="text-sm text-blue-900" data-id="element-368">
                  <strong data-id="element-369">Próximos passos:</strong>
                  <br data-id="element-370" />
                  1. Você receberá um email de confirmação
                  <br data-id="element-371" />
                  2. Nossa equipe entrará em contato para agendar
                  <br data-id="element-372" />
                  3. Prepare o imóvel para o serviço agendado
                </p>
              </div>
              <button onClick={handleClose} className="btn-primary" data-id="element-373">
                Fechar
              </button>
            </div>}
        </div>
      </div>
    </div>;
};
export default ServiceBookingModal;